import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { TasksAiEditor } from '@/ai-editor';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  User,
  Clock,
  Paperclip,
  Plus,
  X,
  Flag,
  Minus,
  ChevronUp,
  ChevronsUp,
  Check,
  Repeat,
} from 'lucide-react';

export function EditTaskSheet({ open, onOpenChange, task, onUpdateTask }) {
  const { userProfile } = useAuth();
  const [taskName, setTaskName] = useState('');
  const [description, setDescription] = useState('');
  const [linkToWorkflow, setLinkToWorkflow] = useState(false);
  const [subtasks, setSubtasks] = useState([]);
  const [newSubtaskText, setNewSubtaskText] = useState('');
  const [dueDate, setDueDate] = useState(null);
  const [priority, setPriority] = useState(null);
  const [recurrence, setRecurrence] = useState('none');

  // Recurrence configuration
  const recurrenceOptions = [
    { value: 'none', label: 'Does not repeat' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Every 2 weeks' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' },
  ];

  // Priority configuration
  const priorityOptions = [
    { value: 'low', label: 'Low', icon: Minus, color: 'text-gray-500', bg: 'bg-gray-100' },
    { value: 'medium', label: 'Medium', icon: Flag, color: 'text-blue-500', bg: 'bg-blue-100' },
    { value: 'high', label: 'High', icon: ChevronUp, color: 'text-orange-500', bg: 'bg-orange-100' },
    { value: 'urgent', label: 'Urgent', icon: ChevronsUp, color: 'text-red-500', bg: 'bg-red-100' },
  ];

  // Pre-fill form when task changes
  useEffect(() => {
    if (task) {
      setTaskName(task.name || '');
      setDescription(task.description || '');
      setLinkToWorkflow(task.link_to_workflow || false);
      setSubtasks(task.subtasks || []);
      setDueDate(task.due_date ? new Date(task.due_date) : null);
      setPriority(task.priority || null);
      setRecurrence(task.recurrence_type || 'none');
    }
  }, [task]);

  const addSubtask = () => {
    if (newSubtaskText.trim()) {
      setSubtasks([...subtasks, { id: Date.now(), text: newSubtaskText.trim(), completed: false }]);
      setNewSubtaskText('');
    }
  };

  const removeSubtask = (id) => {
    setSubtasks(subtasks.filter(st => st.id !== id));
  };

  const updateSubtask = (id, text) => {
    setSubtasks(subtasks.map(st => st.id === id ? { ...st, text } : st));
  };

  const handleUpdate = () => {
    const updatedTask = {
      name: taskName,
      description,
      subtasks,
      linkToWorkflow,
      dueDate: dueDate ? dueDate.toISOString() : null,
      priority,
      recurrence_type: recurrence,
    };
    onUpdateTask(task.id, updatedTask);
    onOpenChange(false);
  };

  if (!task) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-4xl overflow-y-auto p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <SheetTitle className="text-sm font-medium tracking-wider text-muted-foreground">
            EDIT TASK
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Quick Actions */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-9 w-9">
              <User className="h-4 w-4" />
            </Button>
            <Avatar className="h-9 w-9 border">
              <AvatarImage src={userProfile?.avatar_url} />
              <AvatarFallback className="text-xs">{userProfile?.first_name?.[0] || 'U'}</AvatarFallback>
            </Avatar>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={dueDate ? "default" : "outline"}
                  size={dueDate ? "sm" : "icon"}
                  className={dueDate ? "gap-2 h-9" : "h-9 w-9"}
                >
                  <Clock className="h-4 w-4" />
                  {dueDate && (
                    <span>
                      {dueDate.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dueDate}
                  onSelect={setDueDate}
                  initialFocus
                />
                {dueDate && (
                  <div className="p-3 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => setDueDate(null)}
                    >
                      Clear date
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>

            {/* Priority Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                {priority ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className={`gap-2 h-9 ${priorityOptions.find(p => p.value === priority)?.color}`}
                  >
                    {(() => {
                      const opt = priorityOptions.find(p => p.value === priority);
                      const Icon = opt?.icon || Flag;
                      return <Icon className="h-4 w-4" />;
                    })()}
                    {priorityOptions.find(p => p.value === priority)?.label}
                  </Button>
                ) : (
                  <Button variant="outline" size="icon" className="h-9 w-9">
                    <Flag className="h-4 w-4" />
                  </Button>
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>Priority</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {priorityOptions.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <DropdownMenuItem
                      key={opt.value}
                      onClick={() => setPriority(opt.value)}
                      className="flex items-center gap-2"
                    >
                      <Icon className={`h-4 w-4 ${opt.color}`} />
                      <span>{opt.label}</span>
                      {priority === opt.value && (
                        <Check className="h-4 w-4 ml-auto" />
                      )}
                    </DropdownMenuItem>
                  );
                })}
                {priority && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setPriority(null)}
                      className="text-muted-foreground"
                    >
                      Clear priority
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Recurrence Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                {recurrence !== 'none' ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 h-9 text-purple-600"
                  >
                    <Repeat className="h-4 w-4" />
                    {recurrenceOptions.find(r => r.value === recurrence)?.label}
                  </Button>
                ) : (
                  <Button variant="outline" size="icon" className="h-9 w-9">
                    <Repeat className="h-4 w-4" />
                  </Button>
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>Recurrence</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {recurrenceOptions.map((opt) => (
                  <DropdownMenuItem
                    key={opt.value}
                    onClick={() => setRecurrence(opt.value)}
                    className="flex items-center gap-2"
                  >
                    <span>{opt.label}</span>
                    {recurrence === opt.value && (
                      <Check className="h-4 w-4 ml-auto" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="task-name" className="text-sm font-medium">
              Name
            </Label>
            <Input
              id="task-name"
              placeholder="Task name"
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              className="h-11 px-4 text-base focus-visible:ring-2"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Description</Label>
            <TasksAiEditor
              value={description}
              onChange={setDescription}
              placeholder="What is this task about?"
            />
          </div>

          {/* Subtasks */}
          <div className="space-y-3">
            {subtasks.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs font-medium tracking-wider text-muted-foreground">
                  SUBTASKS
                </Label>
                <div className="space-y-2">
                  {subtasks.map((subtask, index) => (
                    <div key={subtask.id} className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground w-6">
                        {index + 1}
                      </span>
                      <Checkbox checked={subtask.completed} disabled />
                      <Input
                        value={subtask.text}
                        onChange={(e) => updateSubtask(subtask.id, e.target.value)}
                        className="flex-1 h-9"
                        placeholder="Subtask description"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9"
                        onClick={() => removeSubtask(subtask.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Input
                value={newSubtaskText}
                onChange={(e) => setNewSubtaskText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addSubtask();
                  }
                }}
                placeholder="Add a subtask"
                className="flex-1 h-9"
              />
              <Button
                variant="outline"
                size="sm"
                className="gap-2 h-9"
                onClick={addSubtask}
                disabled={!newSubtaskText.trim()}
              >
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>
          </div>

          {/* Add Attachment */}
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="gap-2 text-muted-foreground hover:text-foreground h-9 -ml-2"
            >
              <Paperclip className="h-4 w-4" />
              Add attachment
            </Button>
          </div>

          {/* Link to Workflow Run */}
          <div className="flex items-center justify-between py-2">
            <Label
              htmlFor="link-workflow"
              className="text-sm font-normal cursor-pointer"
            >
              Link to a Workflow Run
            </Label>
            <Switch
              id="link-workflow"
              checked={linkToWorkflow}
              onCheckedChange={setLinkToWorkflow}
            />
          </div>
        </div>

        {/* Actions - Fixed at bottom */}
        <div className="border-t px-6 py-4 mt-auto bg-background">
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="h-10 px-4"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={!taskName.trim()}
              className="h-10 px-6"
            >
              Save Changes
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
